import { Button, FormsInputDropdown, Icon, Modal } from "@platform/components";
import { challengeOptions } from "../org-business-challenges-form/consts";
import { useEffect, useMemo, useState } from "react";
import { BusinessChallenges } from "@platform/client";
import { FieldError, SubmitHandler, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { ApplicationMultilineInput } from "../../../application/components/ApplicationMultilineInput";
import { BusinessChallengesEnum, ChallengeUpdateArgsChallengeNumber } from "@platform/client/schemas";

export type ViewModeProps = "view"|"edit"|"create";

export type ChallengeModalFormValues = {
  challengeArea: string | null;
  challengeContext: string | null;
};

interface EditModalProps {
    open: boolean;
    onClose: () => void;
    challengeNumber: ChallengeUpdateArgsChallengeNumber,
    organizationId: number,
    challengeId: number,
    viewMode?: ViewModeProps;
    challenge?: string;
    challengeContext?: string;
};

const EditModal = ({
    open,
    onClose,
    challengeNumber,
    organizationId,
    challengeId,
    challenge="",
    challengeContext="",
    viewMode="view"
}: EditModalProps) => {
    const storageKey = `project_suggestion_${organizationId}_${challengeId}_${challengeNumber}`;
    const [isEditing, setIsEditing] = useState(false);

    const defaultValues = {
        challengeArea: challenge,
        challengeContext: challengeContext,
    };
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = useForm<ChallengeModalFormValues>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: defaultValues,
    });
    const queryClient = useQueryClient();
    const updateChallengeMutator = BusinessChallenges.useUpdateOrganizationBusinessChallenge({
        mutation: {
        onSuccess: () => {
            queryClient.invalidateQueries({
            queryKey: BusinessChallenges.getGetOrganizationBusinessChallengesQueryKey(
                organizationId,
            ),
            });
            onClose();
        },
        },
    });

    const selectedChallengeArea = watch("challengeArea");
    const selectedchallengeContext = watch("challengeContext");
    const challengeContextLength = selectedchallengeContext?.length || 0;
    const isOverMaxLength = challengeContextLength > 1000;
    const maxLengthError: FieldError | undefined = isOverMaxLength
        ? { type: 'maxLength', message: 'Maximum length exceeded.' }
        : undefined;

    useEffect(() => {
        if (selectedChallengeArea !== challenge || selectedchallengeContext != challengeContext) {
            setIsEditing(true);
        }
    }, [challenge, challengeContext, selectedChallengeArea, selectedchallengeContext])

    const submit: SubmitHandler<ChallengeModalFormValues> = (data) => {
        if (data.challengeArea && data.challengeContext) {
            updateChallengeMutator.mutate({
                organizationId: organizationId,
                data: {
                    challenge_number: challengeNumber,
                    challenge_value: data.challengeArea as BusinessChallengesEnum,
                    challenge_context: data.challengeContext,
                },
            });
            try {
                localStorage.removeItem(storageKey);
                localStorage.removeItem(storageKey + "_submitted");
            } catch { /* ignore */ }
        }
    };

    const hasCachedSuggestion = (() => {
        try {
            return !!localStorage.getItem(storageKey);
        } catch {
            return false;
        }
    })();

    const getTitle = useMemo(() => {
        const titleMap = {
            "view": "Your",
            "edit": "Edit",
            "create": "Add a"
        };

        return titleMap[viewMode] + " business challenge";
    }, [viewMode])

    return (
        <form onSubmit={handleSubmit(submit)}>
            <Modal ignoreExtraPadding isOpen={open} onClose={() => onClose()} variant="dialog" title={getTitle}>
                <p className="text-md text-gray-600 mb-3">
                    Review or update this challenge to make sure it represents your current priorities.
                </p>
                <div className="mb-3">
                    <FormsInputDropdown
                        control={control}
                        label='Challenge area'
                        name='challengeArea'
                        type='default'
                        inputDisabled
                        placeholder="Select a challenge area..."
                        items={challengeOptions}
                        error={errors.challengeArea}
                        rules={{ required: 'Select a challenge your organization needs help with to continue' }}
                    />
                </div>
                <div className="h-50">
                    <ApplicationMultilineInput
                        control={control}
                        label='Tell us more about this challenge'
                        name='challengeContext'
                        placeholder="For example, we're trying to… But we're having trouble because… And it's making things harder by…"
                        formsError={errors.challengeContext ?? maxLengthError}
                        rules={{ maxLength: 1000, required: 'Tell us more about this challenge to continue' }}
                        stretchHeight
                        extendedHeight
                        extendedHeightValue="h-[200px]"
                    />
                </div>
                <p className="text-sm text-gray-600 mb-3">
                    {selectedchallengeContext?.length || 0}/1000 characters
                </p>
                {viewMode !== "create" && hasCachedSuggestion && isEditing ? (
                    <div className="flex bg-warning-30 border-1 h-20 items-center border-warning-100 rounded-xl mb-3">
                        <Icon type="alert-triangle" size="20" additionalClassNames="mx-2" stroke='warning-500' />
                        <p className="text-sm text-warning-600">
                            <b>Saving will reset this project.</b> Updating this challenge will remove your generated project.
                        </p>
                    </div>
                ) : (
                    <div className="flex bg-secondary-brand-25 border-1 h-18 border-gray-200 mb-3">
                        <Icon type={viewMode === "create" ? "lightbulb-02" : "info-circle"} size="40" additionalClassNames="mx-2 mt-2" />
                        <p className="text-sm text-gray-600 flex items-center">
                            {
                                viewMode === "create" ?
                                (<p><b>Tip:</b> Share as much or as little as you'd like. The more context you share, the more tailored your project will be.</p>):
                                (<p>Once you save these updates, you can generate a new project. This will not change your submitted or published projects.</p>)
                            }
                        </p>
                    </div>
                )}
                {viewMode === "view" && !isEditing ? (
                    <div className="flex justify-end">
                        <Button variant="primary" size="md" onClick={() => onClose()}>
                            {hasCachedSuggestion && isEditing ? "Keep current project" : "Close"}
                        </Button>
                    </div>
                ):(
                    <div className="flex justify-end">
                        <Button variant="secondary-gray" size="md" onClick={() => onClose()} additionalClassnames="mr-2">Cancel</Button>
                        <Button variant="primary" size="md" type='submit'>Save Changes</Button>
                    </div>
                )}
            </Modal>
        </form>
    )
}

export default EditModal;